import { MeasurementTypeI18N } from '@the-real-insight/tri-model';
import Mustache from 'mustache';

import { BRAND } from '@/brand/brand';
import { correctedMeasurementLabel } from './measurementLabelFix';

/**
 * Was eine Alarmnachricht nennen kann.
 *
 * Spiegel von `ALERT_PLACEHOLDERS` aus tri-server `src/alertTemplateContext.ts` (#1222), Semantik in
 * tri-server `docs/alert-messages.md`. Das Portal kann tri-server nicht importieren, also steht die
 * Liste hier ein zweites Mal — wer sie dort ändert, ändert sie hier mit. Ein hier angebotener Name,
 * den der Server nicht auflöst, rendert im Alarm als Lücke, und genau diese Lücke ist #1183: Der
 * Feldtest schrieb `{{asset.id}}` und bekam achtzehnmal denselben Satz ohne Gerät darin.
 *
 * Roher Schlüssel und Beschriftung stehen nebeneinander: `condition`/`metric` bleiben, wogegen eine
 * Regel geschrieben ist (`accelerationY < 7`), und `conditionLabel`/`metricLabel`/`unit` sagen
 * dasselbe für den Empfänger (`Beschleunigung Y < 7 m/s²`) — beides dokumentiert der Server in
 * `docs/alert-messages.md`, und wer hier nur das eine Paar führt, kann das andere nicht speichern.
 *
 * Zwei Namen, die sich ähnlich lesen und verschiedene Dinge sind:
 *
 *  - `id` ist die **aufgedruckte** Nummer des Geräts (Geschäfts-Id), das Einzige, womit ein
 *    Techniker etwas anfangen kann. Lässt sie sich nicht auflösen, bleibt sie leer — nie ersatzweise
 *    die interne Kennung.
 *  - `_id`/`assetObjectId` ist die interne Kennung (Mongo-Id), die der Portal-Link braucht.
 *
 * `link` bleibt leer, solange der Server keine Portal-Adresse kennt (`PORTAL_ROOT_URL`); die
 * Nachricht lässt die Zeile dann weg.
 */
export interface AlertPlaceholder {
    /** Der Name zwischen den Klammern — `asset.id`, nicht `{{asset.id}}`. */
    name: string;
    /** Schlüssel der Kurzbeschreibung, relativ zu {@link ALERT_TEXT_NAMESPACE}. */
    descriptionKey: string;
}

/** Wo die Oberflächentexte dieses Panels in `de.json`/`en.json` stehen. */
export const ALERT_TEXT_NAMESPACE = 'settingsPanel.settingsPanel.alertManagementPanel.';

export const ALERT_PLACEHOLDERS: AlertPlaceholder[] = [
    { name: 'id', descriptionKey: 'placeholderId' },
    { name: '_id', descriptionKey: 'placeholderObjectId' },
    { name: 'assetObjectId', descriptionKey: 'placeholderAssetObjectId' },
    { name: 'deviceId', descriptionKey: 'placeholderDeviceId' },
    { name: 'name', descriptionKey: 'deviceName' },
    { name: 'asset.id', descriptionKey: 'placeholderId' },
    { name: 'asset.name', descriptionKey: 'deviceName' },
    { name: 'asset.product', descriptionKey: 'placeholderProduct' },
    { name: 'asset.building', descriptionKey: 'placeholderBuilding' },
    { name: 'asset.floor', descriptionKey: 'deviceFloor' },
    { name: 'asset.space', descriptionKey: 'deviceSpace' },
    { name: 'asset.address', descriptionKey: 'deviceLocation' },
    { name: 'ruleName', descriptionKey: 'placeholderRuleName' },
    { name: 'condition', descriptionKey: 'placeholderCondition' },
    { name: 'conditionLabel', descriptionKey: 'placeholderConditionLabel' },
    { name: 'threshold', descriptionKey: 'placeholderThreshold' },
    { name: 'metric', descriptionKey: 'metric' },
    { name: 'metricLabel', descriptionKey: 'placeholderMetricLabel' },
    { name: 'unit', descriptionKey: 'placeholderUnit' },
    { name: 'value', descriptionKey: 'metricValue' },
    { name: 'date', descriptionKey: 'dateOfAlarm' },
    { name: 'time', descriptionKey: 'timeOfAlarm' },
    { name: 'timestamp', descriptionKey: 'exactInstant' },
    { name: 'location', descriptionKey: 'deviceLocation' },
    { name: 'street', descriptionKey: 'deviceStreet' },
    { name: 'streetNumber', descriptionKey: 'deviceStreetNumber' },
    { name: 'postalCode', descriptionKey: 'devicePostalCode' },
    { name: 'city', descriptionKey: 'deviceCity' },
    { name: 'country', descriptionKey: 'deviceCountry' },
    { name: 'floor', descriptionKey: 'deviceFloor' },
    { name: 'space', descriptionKey: 'deviceSpace' },
    { name: 'link', descriptionKey: 'placeholderLink' },
    { name: 'lastUplink', descriptionKey: 'placeholderLastUplink' },
    { name: 'daysSilent', descriptionKey: 'placeholderDaysSilent' },
    { name: 'stage', descriptionKey: 'placeholderStage' },
];

export const ALERT_PLACEHOLDER_NAMES: string[] = ALERT_PLACEHOLDERS.map((placeholder) => placeholder.name);

/**
 * Was der Server außerdem auflöst, ohne dass der Editor es anbietet.
 *
 * `{{asset}}` ohne Feld dahinter ist die naheliegende Abkürzung von `{{asset.id}}`, und der Server
 * beantwortet sie seit #1222 mit derselben Gerätebezeichnung, die auch hinter „Gerät:" steht — der
 * Kontext trägt dafür ein nicht aufzählbares `toString` (tri-server `src/alertTemplateContext.ts`,
 * `docs/alert-messages.md`). Auf der Chip-Liste steht der Name trotzdem nicht: Er ist kein Wert,
 * sondern die Gruppe, zu der die Punktnamen gehören. Als **unbekannt** gemeldet werden darf er aber
 * erst recht nicht — das verweigerte einer Bestandsregel wie `Druck {{value}} bar an {{asset}}` das
 * Speichern, mit der Begründung, der Platzhalter bliebe leer, was er nicht tut.
 */
export const ACCEPTED_PLACEHOLDER_NAMES: string[] = [...ALERT_PLACEHOLDER_NAMES, 'asset'];

/**
 * Die Platzhalter, die ein Gerät benennen.
 *
 * Steht keiner davon im Text, hängt der Server das Gerät selbst an (`withDeviceLine`/
 * `withDeviceSubject`/`withSMSDevicePrefix` in tri-server) — die Nachricht geht also nicht ohne
 * Kennzeichen raus, sieht aber anders aus, als der Text sie geschrieben hat. Deshalb ein Hinweis
 * und keine Sperre.
 *
 * Gespiegelt von `namesTheDevice` (tri-server `src/alertDefaultMessage.ts`), das gegen `asset.id`,
 * `id`, `deviceId`, `_id` **und** die Bezeichnung selbst (`device`/`smsDevice`) prüft:
 *
 *  - `_id`/`assetObjectId` zählen mit: Der Server sucht sie an Wortgrenzen.
 *  - `asset` zählt mit, weil es zu genau dieser Bezeichnung rendert.
 *  - `link` zählt **nicht**, obwohl er die interne Kennung hinter `selectedEntity=` trägt. Kennt
 *    der Server keine Portal-Adresse (`PORTAL_ROOT_URL`), rendert er leer — `namesTheDevice` findet
 *    dann nichts, der Server hängt das Gerät doch an, und der Editor hätte das Gegenteil
 *    versprochen. Ob ein Hinweis erscheint, hängt sonst an einer Umgebungsvariablen, die niemand
 *    im Portal sieht; ein Hinweis zu viel ist harmloser als eine Nachricht, die anders aussieht als
 *    angekündigt.
 *  - `name`/`asset.name` zählen **nicht**: Gesucht wird die ganze Zeichenkette
 *    `FE-4512 (Löscher Treppenhaus)`, und der bloße Name trifft sie nur bei einem Gerät ohne
 *    aufgedruckte Nummer. Ein Hinweis zu viel in diesem Fall ist harmloser als ein fehlender in
 *    jedem anderen.
 */
export const DEVICE_PLACEHOLDERS: string[] = [
    'asset',
    'asset.id',
    'id',
    '_id',
    'assetObjectId',
    'deviceId',
];

/**
 * Beispieldaten für Vorschau und Testversand — ein Wert je Platzhalter.
 *
 * Alle Namen sind belegt, damit die Vorschau die Form der echten Nachricht zeigt statt eine mit
 * Löchern: Eine Vorschau, die die Hälfte still weglässt, bringt der Autorin genau die falsche
 * Lehre über ihr Template bei.
 */
export const ALERT_SAMPLE_CONTEXT = {
    id: 'FE-4512',
    _id: '66c1f0a2b5d4e8f9a1b2c3d4',
    assetObjectId: '66c1f0a2b5d4e8f9a1b2c3d4',
    deviceId: '70B3D5E75E00A1B2',
    name: 'Löscher Treppenhaus',
    asset: {
        id: 'FE-4512',
        name: 'Löscher Treppenhaus',
        product: 'Feuerlöscher PG 6',
        building: 'Haus Nord',
        floor: 'EG',
        space: 'Flur Nord',
        address: 'Beilsteinerweg 22, 56479 Seck',
    },
    ruleName: 'Druckabfall Feuerlöscher',
    condition: 'pressure < 12',
    conditionLabel: 'Druck < 12 bar',
    threshold: '12',
    metric: 'pressure',
    metricLabel: 'Druck',
    unit: 'bar',
    value: '11,4',
    date: '12.12.2024',
    time: '23:45',
    timestamp: '2024-12-12T22:45:00.000Z',
    location: 'Beilsteinerweg 22, 56479 Seck',
    street: 'Beilsteinerweg',
    streetNumber: '22',
    postalCode: '56479',
    city: 'Seck',
    country: 'DE',
    floor: 'EG',
    space: 'Flur Nord',
    lastUplink: '05.09.2026',
    daysSilent: '31',
    stage: 'im Gebäude in Betrieb',
    // The preview links to the portal the user is on: a FireIQ alert does not point to The Real Insight.
    link: `${BRAND.portalRootURL}/entity?selectedEntityType=Asset&selectedEntity=66c1f0a2b5d4e8f9a1b2c3d4`,
};

/**
 * `{{asset}}` ohne Feld dahinter — dieselbe Bezeichnung, die der Server hinter „Gerät:" schreibt
 * (`device` in tri-server `src/alertDefaultMessage.ts`: aufgedruckte Nummer, Name in Klammern).
 *
 * Nicht aufzählbar wie dort: Eine Methode ist kein Platzhalter, und die Vorschau soll das Objekt
 * genauso behandeln wie der Server.
 */
Object.defineProperty(ALERT_SAMPLE_CONTEXT.asset, 'toString', {
    value: () => 'FE-4512 (Löscher Treppenhaus)',
    enumerable: false,
    writable: true,
    configurable: true,
});

/**
 * The same sample for an English UI: names, labels and the number/date notation are English.
 *
 * Kept German on purpose: `stage` (the server writes "im Werk aktiviert" / "im Gebäude in Betrieb"
 * whatever language the portal is in) and the ISO `timestamp`, `country` code and portal `link`.
 */
const ALERT_SAMPLE_CONTEXT_EN = {
    ...ALERT_SAMPLE_CONTEXT,
    name: 'Stairwell extinguisher',
    asset: {
        id: 'FE-4512',
        name: 'Stairwell extinguisher',
        product: 'Fire extinguisher PG 6',
        building: 'North building',
        floor: 'GF',
        space: 'North hallway',
        address: 'Beilsteinerweg 22, 56479 Seck',
    },
    ruleName: 'Extinguisher pressure drop',
    conditionLabel: 'Pressure < 12 bar',
    metricLabel: 'Pressure',
    value: '11.4',
    date: '12/12/2024',
    time: '11:45 PM',
    floor: 'GF',
    space: 'North hallway',
    lastUplink: '09/05/2026',
};

Object.defineProperty(ALERT_SAMPLE_CONTEXT_EN.asset, 'toString', {
    value: () => 'FE-4512 (Stairwell extinguisher)',
    enumerable: false,
    writable: true,
    configurable: true,
});

/** The sample values for preview, picker examples and test sends, in the portal's language. */
export const alertSampleContext = (language?: string, deviceAlarm?: string): typeof ALERT_SAMPLE_CONTEXT => {
    const english = (language ?? 'de').startsWith('en');
    const base = english ? ALERT_SAMPLE_CONTEXT_EN : ALERT_SAMPLE_CONTEXT;

    return deviceAlarm ? deviceAlarmSample(base, english ? 'en' : 'de', deviceAlarm) : base;
};

/**
 * The same sample device, but the alert is an alarm the device raised itself (`deviceAlarm` = its
 * name, e.g. `deviceMoving`): there is no threshold or reading, only the alarm's name — what
 * `{{metricLabel}}` says in a real device alarm. A real alert names the one alarm that fired, so the
 * preview shows the alarm the user just chose.
 */
const deviceAlarmSample = (
    base: typeof ALERT_SAMPLE_CONTEXT,
    language: 'de' | 'en',
    alarm: string,
): typeof ALERT_SAMPLE_CONTEXT => {
    const names: any = MeasurementTypeI18N;
    const label: string = correctedMeasurementLabel(names[language]?.[alarm] ?? alarm);

    return {
        ...base,
        ruleName: language === 'en' ? 'Device alarm' : 'Alarm vom Gerät',
        condition: `${alarm} > 0`,
        conditionLabel: `${label} > 0`,
        threshold: '0',
        metric: alarm,
        metricLabel: label,
        unit: '',
        value: '1',
    } as typeof ALERT_SAMPLE_CONTEXT;
};

/** Ein Platzhalter, wie er im Text steht — geschrieben, wie ihn die Meldung zitiert. */
export const placeholderTag = (name: string): string => `{{${name}}}`;

/**
 * Mustache-Steuerzeichen: Abschnitte, Kommentare, Partials, eigene Trenner.
 *
 * Nichts davon ist ein Platzhalter, und `{{#formatDate}}` als „unbekannt" zu melden, hieße
 * Bestandstemplates das Speichern zu verweigern.
 */
const CONTROL_SIGILS = '#^/!>=';

const TAG = /\{\{([^{}]*)\}\}/g;

/** Jeder Platzhaltername, der im Text steht — in Reihenfolge, Wiederholungen eingeschlossen. */
export const placeholdersOf = (template?: string | null): string[] => {
    const names: string[] = [];

    if (!template) {
        return names;
    }

    // `TAG` trägt `g`, also auch `lastIndex` — eine eigene Instanz je Aufruf, damit zwei Aufrufe
    // sich nicht gegenseitig die Suchposition verstellen.
    const pattern = new RegExp(TAG.source, 'g');
    let match = pattern.exec(template);

    while (match) {
        const inner = match[1].trim();
        const name = inner.startsWith('&') ? inner.slice(1).trim() : inner;

        if (name && !CONTROL_SIGILS.includes(inner.charAt(0))) {
            names.push(name);
        }

        match = pattern.exec(template);
    }

    return names;
};

/** Abstand nach Levenshtein — genug, um einen Tippfehler von einem anderen Namen zu trennen. */
const editDistance = (left: string, right: string): number => {
    const a = left.toLowerCase();
    const b = right.toLowerCase();
    let previous = Array.from({ length: b.length + 1 }, (_unused, index) => index);

    for (let i = 1; i <= a.length; i++) {
        const current = [i];

        for (let j = 1; j <= b.length; j++) {
            current[j] = Math.min(
                previous[j] + 1,
                current[j - 1] + 1,
                previous[j - 1] + (a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1),
            );
        }

        previous = current;
    }

    return previous[b.length];
};

/**
 * Der nächstliegende bekannte Name — oder keiner.
 *
 * Die Schranke wächst mit der Länge (ein Drittel des Namens), damit `asset.idd` → `asset.id`
 * vorgeschlagen wird, `foo` aber nicht zu `floor` geraten wird. Ein falscher Vorschlag ist
 * schlimmer als keiner: Er klingt nach einer Antwort.
 *
 * Vorgeschlagen wird nur, was der Editor auch anbietet: Ein Vorschlag, den die Chips daneben nicht
 * kennen, schickte die Autorin zum Abtippen.
 */
export const suggestPlaceholder = (name: string): string | undefined => {
    const limit = Math.max(1, Math.floor(name.length / 3));
    let best: string | undefined;
    let bestDistance = Number.POSITIVE_INFINITY;

    for (const candidate of ALERT_PLACEHOLDER_NAMES) {
        const distance = editDistance(name, candidate);

        if (distance < bestDistance) {
            best = candidate;
            bestDistance = distance;
        }
    }

    return bestDistance <= limit ? best : undefined;
};

export interface UnknownPlaceholder {
    name: string;
    suggestion?: string;
}

/**
 * Was im Text steht und der Server nicht kennt.
 *
 * Geprüft wird gegen {@link ACCEPTED_PLACEHOLDER_NAMES}, nicht gegen die Chip-Liste: Gesperrt
 * gehört, was im Alarm eine Lücke ergibt, und `{{asset}}` ergibt keine.
 *
 * Jeder Name nur einmal, in der Reihenfolge des Textes — dreimal dieselbe Meldung ist keine
 * dreifache Auskunft.
 */
export const unknownPlaceholders = (template?: string | null): UnknownPlaceholder[] => {
    const seen = new Set<string>();
    const unknown: UnknownPlaceholder[] = [];

    for (const name of placeholdersOf(template)) {
        if (ACCEPTED_PLACEHOLDER_NAMES.includes(name) || seen.has(name)) {
            continue;
        }

        seen.add(name);

        const suggestion = suggestPlaceholder(name);

        unknown.push(suggestion ? { name, suggestion } : { name });
    }

    return unknown;
};

/** Ob der Text das Gerät selbst benennt (siehe {@link DEVICE_PLACEHOLDERS}). */
export const namesDevice = (template?: string | null): boolean =>
    placeholdersOf(template).some((name) => DEVICE_PLACEHOLDERS.includes(name));

/**
 * Der Text mit eingesetzten Werten.
 *
 * Ohne HTML-Escaping, wie der Server (`renderAlert`): Beide Kanäle sind Klartext, und aus einem
 * Portal-Link würde sonst `https:&#x2F;&#x2F;…`. Ein Template, das Mustache nicht parsen kann, geht
 * unverändert zurück statt die Vorschau zu sprengen — der Server verschickt es in diesem Fall auch
 * unverändert (`renderMailField`).
 */
export const renderAlertTemplate = (
    template?: string | null,
    context: object = ALERT_SAMPLE_CONTEXT,
): string => {
    if (!template) {
        return '';
    }

    try {
        return Mustache.render(template, context, {}, { escape: (value: unknown) => String(value) });
    } catch {
        return template;
    }
};

/**
 * Einen Platzhalter an der Schreibmarke einsetzen.
 *
 * Gibt den neuen Text und die Stelle zurück, an der die Marke danach steht — hinter dem
 * Eingefügten, damit weitergeschrieben werden kann, wo gerade geschrieben wurde.
 */
export const insertAt = (
    value: string,
    snippet: string,
    start: number,
    end: number,
): { value: string; caret: number } => {
    const from = Math.max(0, Math.min(start, value.length));
    const to = Math.max(from, Math.min(end, value.length));

    return {
        value: `${value.slice(0, from)}${snippet}${value.slice(to)}`,
        caret: from + snippet.length,
    };
};
